import { useTranslations } from "next-intl";
import { Fragment, type ReactNode } from "react";

import { type TourCalendar } from "~/lib/use-tour-calendar";

import { BokunPricingCategoryLabel } from "./bokun-passengers";
import { LinktivityPricingCategoryLabel } from "./linktivity-passengers";
import { PricingCategory } from "./pricing-category";

function AgeRange({
  minAge,
  maxAge,
}: Pick<
  TourCalendar["passengerCategories"][0],
  "minAge" | "maxAge"
>): ReactNode {
  const t = useTranslations("TourDetails");
  if (minAge !== undefined && maxAge !== undefined) {
    return <span>{t("ageRange", { minAge, maxAge })}</span>;
  } else if (minAge !== undefined) {
    return <span>{t("minAge", { minAge })}</span>;
  } else if (maxAge !== undefined) {
    return <span>{t("maxAge", { maxAge })}</span>;
  }
  return null;
}

export function TourPassengerCategories({
  passengerCategories,
  platform,
  onChange,
  disabled = false,
}: {
  platform: "bokun" | "linktivity";
  onChange: (value: { count: number; id: string }) => void;
  passengerCategories: TourCalendar["passengerCategories"];
  disabled?: boolean;
}) {
  const CategoryLabel =
    platform === "bokun"
      ? BokunPricingCategoryLabel
      : LinktivityPricingCategoryLabel;

  return (
    <>
      {passengerCategories.map(({ type, id, count, ...props }) => {
        const nameIsRepeatedCategory =
          props.name &&
          (props.name
            .toLocaleLowerCase()
            .startsWith(CategoryLabel({ type }).toLocaleLowerCase()) ||
            props.name
              .toLocaleLowerCase()
              .startsWith(type.toLocaleLowerCase()));
        let labels = [];
        const hasAgeRange =
          typeof props.minAge === "number" || typeof props.maxAge === "number";
        if (props.name && !nameIsRepeatedCategory && !hasAgeRange) {
          labels.push(props.name);
        }
        if (hasAgeRange) {
          labels.push(<AgeRange key={"age"} {...props} />);
        }
        labels = labels.filter(Boolean);
        return (
          <PricingCategory
            key={id}
            count={count}
            disabled={disabled}
            label={
              <>
                <CategoryLabel type={type} />
                {labels.length > 0 ? (
                  <div className="text-xs font-light text-neutral-500">
                    {labels.map((label, index) => (
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      <Fragment key={`${index}-label`}>
                        {index > 0 ? " - " : null}
                        {label}
                      </Fragment>
                    ))}
                  </div>
                ) : null}
              </>
            }
            onChange={(count) => {
              onChange({ id, count });
            }}
            type={type}
          />
        );
      })}
    </>
  );
}

/*

TODO

- Next available
- Make start time + date + plan not reset each other
- Ensure inventory updates work
-

*/
